import * as Sentry from '@sentry/nextjs';
import getenv from 'getenv';
import { useEffect } from 'react';
import { useRouter } from 'next/router';

import { assertIsLocalePrefix } from '../utils.service';

import type { ConversionId, Value } from './tracify.contracts';
import { getCurrencyByLocalePrefix } from './tracify.helpers';
import { tracifyService } from './tracify.service';

const isRunningInDevelopmentMode: boolean = getenv('NODE_ENV', process.env.NODE_ENV) === 'development';

/**
 * A custom React hook that returns an async function that's capable of calling the `captureConversion()` method on the
 * global Tracify object. The returned function accepts only a single argument - `conversionId` - which is then passed
 * to the target `captureConversion()` method invocation.
 */
export const useTracifyCaptureConversion = (): ((conversionId: ConversionId) => Promise<void>) => {
  const { locale } = useRouter();

  assertIsLocalePrefix(locale);

  /**
   * Calls the `captureConversion()` method on the global Tracify object.
   *
   * @param conversionId - The `ConversionID` that's going to be passed to the Tracify's `captureConversion()` method.
   *
   * @throws Error - In case of problem with sending the conversion to Tracify (only in development mode).
   *
   * @see Tracify.captureConversion
   */
  return async (conversionId: ConversionId) => {
    try {
      await tracifyService.captureConversion({
        conversionId,
        currency: getCurrencyByLocalePrefix(locale),
        email: conversionId,
        value: '0' as Value,
      });
    } catch (tracifyError) {
      const error = new Error(`Failed to submit the conversion to Tracify! ${tracifyError}`);

      if (isRunningInDevelopmentMode) {
        throw error;
      } else {
        Sentry.captureException(error);
      }
    }
  };
};

/**
 * A custom React hook that calls the Tracify's `capturePageView()` method every time the User redirects to a new page.
 */
export function useTracifyPageViews() {
  const { asPath } = useRouter();

  useEffect(() => {
    void tracifyService.capturePageView();
  }, [asPath]);
}
